import {cancel, HybirdBridge, invoke, on, ready} from '@hybird/core/bridge/hybirdBridge'
import {BridgeMethod} from "@hybird/core/bridge/core";
import {defaultError, WebError} from "@hybird/core/error";

export const permissions = {
  'calendar': 0,
  'camera': 1,
  'contacts': 2,
  'location': 3,
  'locationAlways': 4,
  'locationWhenInUse': 5,
  'mediaLibrary': 6,
  'microphone': 7,
  'phone': 8,
  'photos': 9,
  'photosAddOnly': 10,
  'reminders': 11,
  'sensors': 12,
  'sms': 13,
  'speech': 14,
  'storage': 15,
  'ignoreBatteryOptimizations': 16,
  'notification': 17,
  'access_media_location': 18,
  'activity_recognition': 19,
  'unknown': 20,
  'bluetooth': 21,
  'manageExternalStorage': 22,
  'systemAlertWindow': 23,
  'requestInstallPackages': 24,
  'appTrackingTransparency': 25,
  'criticalAlerts': 26,
  'accessNotificationPolicy': 27,
  'bluetoothScan': 28,
  'bluetoothAdvertise': 29,
  'bluetoothConnect': 30,
  'nearbyWifiDevices': 31,
  'videos': 32,
  'audio': 33,
  'scheduleExactAlarm': 34,
  'sensorsAlways': 35,
  'calendarWriteOnly': 36,
  'calendarFullAccess': 37,
  'assistant': 38,
  'backgroundRefresh': 39,
};

export class FlutterCore extends BridgeMethod {
  constructor() {
    super()
    HybirdBridge.prototype.onHistoryBack = this.onHistoryBack.bind(this)
    HybirdBridge.prototype.offHistoryBack = this.offHistoryBack.bind(this)
    HybirdBridge.prototype.deviceInfo = this.deviceInfo.bind(this)
    HybirdBridge.prototype.jpush = this.jpush.bind(this)
    HybirdBridge.prototype.onBattery = this.onBattery.bind(this)
    HybirdBridge.prototype.getBattery = this.getBattery.bind(this)
    HybirdBridge.prototype.setKeepScreenOn = this.setKeepScreenOn.bind(this)
    HybirdBridge.prototype.onAppLifecycle = this.onAppLifecycle.bind(this)
    HybirdBridge.prototype.offAppLifecycle = this.offAppLifecycle.bind(this)
    HybirdBridge.prototype.checkJsApi = this.checkJsApi.bind(this)
    HybirdBridge.prototype.ready = this.ready.bind(this)
    HybirdBridge.prototype.requestPermission = this.requestPermission.bind(this);
    HybirdBridge.prototype.setBadge = this.setBadge.bind(this);
    HybirdBridge.prototype.setTimeout = this.setTimeout.bind(this);
    HybirdBridge.prototype.setInterval = this.setInterval.bind(this);
    HybirdBridge.prototype.clearInterval = this.clearInterval.bind(this);
  }


  /**
   * 返回上一页
   * @param event{function({})}
   */
  async onHistoryBack(event) {
    await on('onHistoryBack', event, {isOne: true})
    return event
  }

  /**
   * 返回上一页
   * @param event{function({})}
   */
  async offHistoryBack(event) {
    await cancel('onHistoryBack', {event: event, isOne: true})
    return event
  }

  async openAppSettings() {
    return invoke('openAppSettings');
  }

  /**
   * 获取权限
   * @see {permissions}
   * permission 参数由permissions中的定义, 请勿直接传int防止异常情况
   * @param permission 权限id, 允许为空, 当为空时将请求flutter中定义的所有所需权限
   * @return {Promise<{isGranted:boolean, denied:[Number], granted:[Number]}>}
   */
  async requestPermission(permission = undefined) {
    if (!(permission instanceof Array)) {
      permission = [permission];
    }

    const params = await invoke('requestPermission', {params: {permission}});

    return {
      isGranted: this.isGranted(params),
      denied: this.requestPermissionDenied(params),
      granted: this.requestPermissionGranted(params)
    }
  }

  isGranted(params) {
    for (const key in params) {
      if (!params[key]) {
        return false;
      }
    }

    return true;
  }

  requestPermissionDenied(params) {
    const list = [];
    for (const key in params) {
      if (!params[key]) {
        list.push(parseInt(key));
      }
    }

    return list;
  }

  requestPermissionGranted(params) {
    const list = [];
    for (const key in params) {
      if (params[key]) {
        list.push(parseInt(key));
      }
    }

    return list;
  }

  /**
   *
   * @return {
   *   Promise<{
   *         "jpush": {
   *           "registrationID": {},
   *           "appKey": "319b677a623cd48313335ef6"
   *         },
   *         "platform": "android"|"iOS"|"none",
   *         "deviceData": {},
   *       }>
   * }
   */
  async deviceInfo() {
    return invoke('deviceInfo')
  }

  /**
   * 初始化jpush
   * @param onOpenNotification 打开应用的消息
   * @param onReceiveNotification 收到的消息回调
   * @return {Promise<boolean>}
   */
  async jpush({onOpenNotification, onReceiveNotification}) {
    return invoke('jpush', {
      on: {
        onOpenNotification, onReceiveNotification
      }
    })
  }

  async onBattery(callback) {
    return on("onBattery", callback);
  }

  /**
   *
   * @return {Promise<{status, battery}>}
   */
  async getBattery() {
    return invoke("getBattery");
  }

  /**
   * 清理所有推送消息
   * @return {Promise<void>}
   */
  async clearAllNotifications() {
    return invoke('clearAllNotifications')
  }

  /**
   * 清理指定的推送消息
   * @param notificationId 推送id
   * @return {Promise<void>}
   */
  async clearNotification(notificationId) {
    return invoke('clearNotification', {
      params: {
        notificationId
      }
    })
  }

  async setTimeout(onInterval, time) {
    return invoke('setTimeout', {
      params: {
        time
      },
      on: {
        onInterval
      }
    })
  }

  id = 1;

  /**
   *
   * @param time
   * @param onInterval
   * @return {Number}
   */
  setInterval(onInterval, time) {
    const id = this.id = ++this.id;

    invoke('setInterval', {
      params: {
        time, id
      },
      on: {
        onInterval
      }
    })

    return id;
  }

  /**
   *
   * @param id
   * @return {Promise<Number>}
   */
  clearInterval(id) {
    if (id) {
      invoke('clearInterval', {
        params: {
          id
        }
      })
    }
  }

  /**
   * 设置角标
   * @return {Promise<boolean>}
   */
  async setBadge(badge) {
    const flag = await this.requestPermission([permissions.notification]);
    if (flag.isGranted) {
      return invoke('setBadge', {
        params: {badge}
      })
    } else {
      return false;
    }
  }

  async startForegroundTaskService({title, text}) {
    const flag = await flutterCore.requestPermission([permissions.notification]);

    if (!flag.isGranted) {
      throw WebError.code(defaultError.NOT_PERMISSION, {
        msg: "通知权限未授权, 请前往设置授权",
        data: flag.denied
      })
    }

    return invoke('startForegroundTaskService', {
      params: {title, text}
    })
  }

  async stopForegroundTaskService() {
    return invoke('stopForegroundTaskService')
  }

  async onAppLifecycle(event) {
    await on('onAppLifecycle', event, {isSend: true})
    return event
  }

  async offAppLifecycle(event) {
    await cancel('onTest', {event: event, isSend: true})
    return event
  }

  /**
   * 开启屏幕常量
   * @param params{{keepScreenOn: Boolean}} keepScreenOn 是否开启
   * @return {Promise<Boolean>} 开启是否成功
   */
  setKeepScreenOn(params) {
    return invoke('setKeepScreenOn', {
      params
    })
  }

  /**
   * 验证api是否有效
   * @param params{{apis: []}} 验证列表
   * @return {Promise<{string: boolean}>} 验证列表是否可以
   */
  checkJsApi(params) {
    return invoke('checkJsApi', {
      params
    }).then((data) => {
      return data.data
    })
  }

  /**
   * 准备就绪
   * @param event{function}
   * @return {Promise<void>} 可以await
   */
  ready(event) {
    return ready(event)
  }
}

export const flutterCore = new FlutterCore();